import React, { useState, ReactElement } from 'react';
import { t, defineMessages } from '@sm/intl';
import { createURL, getClientEnvironmentDetails } from '@sm/utils';
import { Box } from '@wds/box';
import { IconPlus } from '@wds/icons';
import { List } from '@wds/list';
import { Responsive } from '@wds/responsive';
import { Sheet } from '@wds/sheet';
import { useHeaderTheme } from '../../theme';
import { Title } from '../../components/title';
import { Logo } from '../logo';
import { VariantComponent } from '../../variants';
import { Brand } from '../../types';
import { SubMenuItem } from './SubMenuItem';
import { MobileMenuButton } from './MobileMenuButton';
import { signOut } from '../../../config/MobileLinkInfo.js';

import useStyles from './useStyles';

const COPY = defineMessages({
  CREATE_SURVEY_LABEL: {
    id: 'MobileNav.createSurvey',
    defaultMessage: 'Create survey icon',
    description:
      '[Type: Label][Vis.: low] - Create Survey plus button on mobile',
  },
  MENU_ICON_LABEL: {
    id: 'MobileNav.menuIcon',
    defaultMessage: 'Menu icon',
    description:
      '[Type: Label][Vis.: low] - Mobile navigation menu button icon',
  },
  CLOSE_ICON_LABEL: {
    id: 'MobileNav.closeIcon',
    defaultMessage: 'Close menu icon',
    description:
      '[Type: Label][Vis.: low] - Mobile navigation close button icon',
  },
});

export type MobileNavProps = {
  brand: Brand;
  title?: string;
  userMenu: VariantComponent;
  navItems: VariantComponent;
  infoMenu: VariantComponent;
  create: boolean;
  link: string;
};

const MobileNav = ({
  brand,
  title = '',
  userMenu,
  navItems,
  link,
  infoMenu,
  create,
}: MobileNavProps): ReactElement => {
  const theme = useHeaderTheme();
  const classes = useStyles({ title, theme });

  const [showMobileNav, setShowMobileNav] = useState(false);
  const toggleMobileNav = (): void => setShowMobileNav(!showMobileNav);

  const { isDesktop } = getClientEnvironmentDetails();

  return (
    <Responsive maxWidth="xl" defaultMatches={!isDesktop}>
      <>
        {create && (
          <Responsive maxWidth="md">
            <a
              href={createURL('/create/', { ut_source: 'header' })}
              className={classes.createButton}
            >
              <IconPlus size="xl" title={t(COPY.CREATE_SURVEY_LABEL)} />
            </a>
          </Responsive>
        )}
        <div className={classes.mobileNavContainer}>
          <Box
            className={classes.mobileNavBoxFull}
            display="flex"
            flexAlign="center"
          >
            <Responsive minWidth="md" defaultMatches={!isDesktop}>
              <>
                <MobileMenuButton
                  showMobileNav={showMobileNav}
                  toggleMobileNav={toggleMobileNav}
                  testid="tablet-menu-button"
                  left
                />
                <Logo brand={brand} link={link} />
              </>
            </Responsive>
            <Responsive maxWidth="md" defaultMatches={!isDesktop}>
              <>
                <MobileMenuButton
                  showMobileNav={showMobileNav}
                  toggleMobileNav={toggleMobileNav}
                  left={!title}
                />
                <div className={classes.mobileNavLogo}>
                  <Logo brand={brand} link={link} />
                </div>
              </>
            </Responsive>
            {title && <Title>{title}</Title>}
          </Box>
          <Sheet
            showCloseButton
            onClose={toggleMobileNav}
            show={showMobileNav}
            placement="left"
            offsetTop={48}
          >
            <Box className={classes.mobileNavMenu}>
              <List noRules interactive>
                {navItems}
              </List>
              {infoMenu}
              {!!userMenu && (
                <Responsive maxWidth="md" defaultMatches={!isDesktop}>
                  <List interactive>
                    {userMenu}
                    <SubMenuItem {...signOut()} />
                  </List>
                </Responsive>
              )}
            </Box>
          </Sheet>
        </div>
      </>
    </Responsive>
  );
};

export default MobileNav;
export { MobileNav };
